// sound5/util.js: small helpers shared by the score modules (no DOM access).
export const mtof = (m) => 440 * Math.pow(2, (m - 69) / 12);
export const clamp = (x, a = 0, b = 1) => (x < a ? a : x > b ? b : x);
export const lerp = (a, b, t) => a + (b - a) * t;
export const smoothstep = (a, b, x) => {
  const t = clamp((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};
export const num = (v, d = 0) => (typeof v === 'number' && isFinite(v) ? v : d);

// Seeded PRNG (mulberry32) so offline renders are reproducible.
export function makeRng(seed) {
  let s = (seed >>> 0) || 1;
  const r = () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  r.range = (a, b) => a + (b - a) * r();
  r.pick = (arr) => arr[Math.floor(r() * arr.length) % arr.length];
  r.exp = (rate) => -Math.log(1 - r() * 0.999999) / Math.max(1e-6, rate);
  r.sign = () => (r() < 0.5 ? -1 : 1);
  return r;
}

export function hashString(str) {
  let h = 2166136261 >>> 0;
  const s = String(str || '');
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

// A continuously controlled AudioParam. set() only schedules when the target
// actually moves, so the automation timeline stays short at 60 calls/s.
// Only ever uses setTargetAtTime, which can be re-targeted without jumps.
export class Knob {
  constructor(param, value, rel = 0.01, abs = 1e-4) {
    this.p = param;
    this.v = value;
    this.rel = rel;
    this.abs = abs;
    try { param.value = value; } catch (e) { /* read-only in some engines */ }
  }
  set(v, t, tc) {
    if (!(v === v) || v === this.v) return; // NaN guard
    const d = Math.abs(v - this.v);
    if (v !== 0 && d < Math.max(this.abs, this.rel * Math.abs(v))) return;
    this.v = v;
    try { this.p.setTargetAtTime(v, t, Math.max(0.002, tc)); } catch (e) { /* ignore */ }
  }
  // Re-target from `t`, dropping anything scheduled later (only setTarget
  // events are ever used on knob params, so cancelling never reverts a ramp).
  go(v, t, tc) {
    if (!(v === v)) return;
    this.v = v;
    try {
      this.p.cancelScheduledValues(t);
      this.p.setTargetAtTime(v, t, Math.max(0.002, tc));
    } catch (e) { /* ignore */ }
  }
  jump(v, t) {
    this.v = v;
    try {
      this.p.cancelScheduledValues(t);
      this.p.setValueAtTime(v, t);
    } catch (e) { /* ignore */ }
  }
}

// Connect a -> b -> c ... (old WebKit's connect() returned undefined, so no chaining).
export function chain(...nodes) {
  for (let i = 0; i < nodes.length - 1; i++) nodes[i].connect(nodes[i + 1]);
  return nodes[nodes.length - 1];
}

export function biquad(ctx, type, freq, q) {
  const f = ctx.createBiquadFilter();
  // fixed stereo: Firefox warns (and can glitch) when a filter's input channel count changes as voices come and go
  try { f.channelCount = 2; f.channelCountMode = 'explicit'; } catch (e) { /* ignore */ }
  f.type = type;
  f.frequency.value = freq;
  if (q != null) f.Q.value = q;
  return f;
}

// Seamlessly looping mono noise, normalised to `rms`.
export function noiseBuffer(ctx, seconds, kind, rng, rms = 0.25, rate = 0) {
  const sr = rate || ctx.sampleRate, len = Math.max(256, Math.floor(sr * seconds));
  const fade = Math.min(len >> 3, Math.floor(sr * 0.08));
  const tmp = new Float32Array(len + fade);
  let b0 = 0, b1 = 0, b2 = 0, br = 0;
  for (let i = 0; i < tmp.length; i++) {
    const w = rng() * 2 - 1;
    if (kind === 'pink') {
      b0 = 0.99765 * b0 + w * 0.099046;
      b1 = 0.963 * b1 + w * 0.2965164;
      b2 = 0.57 * b2 + w * 1.0526913;
      tmp[i] = b0 + b1 + b2 + w * 0.1848;
    } else if (kind === 'brown') {
      br = (br + 0.02 * w) / 1.02;
      tmp[i] = br;
    } else tmp[i] = w;
  }
  for (let i = 0; i < fade; i++) {
    const a = i / fade;
    tmp[i] = tmp[i] * a + tmp[len + i] * (1 - a);
  }
  let m = 0, e = 0;
  for (let i = 0; i < len; i++) m += tmp[i];
  m /= len;
  for (let i = 0; i < len; i++) { tmp[i] -= m; e += tmp[i] * tmp[i]; }
  const s = rms / Math.sqrt(e / len || 1);
  const b = ctx.createBuffer(1, len, sr), d = b.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = tmp[i] * s;
  return b;
}

// A hall impulse response: stereo decorrelated noise, 18 ms pre-delay, soft
// onset, exponential decay and a tail that darkens as it decays (air
// absorption). Unit energy per channel so wet levels are predictable.
export function makeIR(ctx, seconds, rng) {
  const sr = ctx.sampleRate, len = Math.max(256, Math.floor(sr * seconds));
  const buf = ctx.createBuffer(2, len, sr);
  const pre = Math.floor(0.018 * sr), T60 = seconds * 0.8, decay = 6.9078 / (T60 * sr);
  const onset = 0.05 * sr, TWO_PI = Math.PI * 2;
  let energy = 0;
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    let l1 = 0, l2 = 0, a = 0.5;
    for (let i = pre; i < len; i++) {
      const n = i - pre;
      if ((n & 63) === 0) {
        const fc = 900 + 7600 * Math.exp(-(n / sr) / (seconds * 0.18));
        a = 1 - Math.exp(-TWO_PI * fc / sr);
      }
      const w = rng() * 2 - 1;
      l1 += a * (w - l1);
      l2 += a * (l1 - l2);
      const v = l2 * Math.exp(-decay * n) * (n < onset ? n / onset : 1);
      d[i] = v;
      energy += v * v;
    }
  }
  const s = 1 / Math.sqrt(energy / 2 || 1);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] *= s;
  }
  return buf;
}

// Stereo placement that also works where StereoPannerNode is missing (old Safari).
export function makePan(ctx, x) {
  try {
    if (ctx.createStereoPanner) {
      const p = ctx.createStereoPanner();
      p.pan.value = x;
      return { node: p, pan: p.pan };
    }
  } catch (e) { /* fall through */ }
  const p = ctx.createPanner();
  p.panningModel = 'equalpower';
  const a = clamp(x, -1, 1) * Math.PI / 2;
  try { p.setPosition(Math.sin(a), 0, -Math.cos(a)); } catch (e) { /* ignore */ }
  return { node: p, pan: null };
}

// A constant signal whose value is an AudioParam (ConstantSourceNode, or a
// looped buffer of ones through a gain where that node is missing).
export function makeConstant(ctx, value) {
  if (ctx.createConstantSource) {
    const c = ctx.createConstantSource();
    c.offset.value = value;
    c.start();
    return { node: c, param: c.offset };
  }
  const b = ctx.createBuffer(1, 128, ctx.sampleRate);
  b.getChannelData(0).fill(1);
  const s = ctx.createBufferSource();
  s.buffer = b;
  s.loop = true;
  const g = ctx.createGain();
  g.gain.value = value;
  s.connect(g);
  s.start();
  return { node: g, param: g.gain };
}

// Unity-gain below `knee`, then a tanh knee that never exceeds `ceil`
// (0.86 = -1.3 dBFS). Odd length so 0 maps exactly to 0 (no DC).
export function softClipCurve(n = 4097, knee = 0.62, ceil = 0.86) {
  const c = new Float32Array(n), room = ceil - knee;
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1, a = Math.abs(x);
    const y = a <= knee ? a : knee + room * Math.tanh((a - knee) / room);
    c[i] = x < 0 ? -y : y;
  }
  return c;
}

// Gentle saturation: adds 2nd/3rd-order colour so sub-bass is heard on phones.
export function warmCurve(n = 2049, drive = 2.2, asym = 0.12) {
  const c = new Float32Array(n), norm = Math.tanh(drive);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    c[i] = (Math.tanh(drive * (x + asym * x * x)) - Math.tanh(0)) / norm;
  }
  // remove the DC the asymmetry adds at x = 0
  const mid = c[(n - 1) >> 1];
  for (let i = 0; i < n; i++) c[i] -= mid;
  return c;
}

// Additive PeriodicWave from harmonic amplitudes (sine phase).
export function makeWave(ctx, amps) {
  const n = amps.length + 1, re = new Float32Array(n), im = new Float32Array(n);
  for (let k = 1; k < n; k++) im[k] = amps[k - 1];
  return ctx.createPeriodicWave(re, im);
}
